import { Injectable, inject } from '@angular/core';
import {
    createDuplicatedAeBoxAnchor,
    createDuplicatedAllocationElementDiagramAnchor,
    createDuplicatedFvBoxAnchor,
    createDuplicatedNoteBoxAnchor,
    getNextTemporaryAnchorKey,
    mergeNewAndExistingObjects,
    removeNestedObjects,
} from './static-diagram.util';
import { AeBoxAnchor } from 'src/models/anchor-and-version/ae-box.model';
import { AeBoxService } from 'src/services/anchor-and-version-services/ae-box.service';
import { AllocationElementDiagramAnchor } from 'src/models/anchor-and-version/allocation-element-diagram.model';
import { AllocationElementDiagramService } from 'src/services/anchor-and-version-services/allocation-element-diagram.service';
import { DirectWirePortDataAnchor } from 'src/models/anchor-and-version/direct-wire-port-data.model';
import { DirectWirePortDataService } from 'src/services/anchor-and-version-services/direct-wire-port-data.service';
import { FvBoxAnchor } from 'src/models/anchor-and-version/fv-box.model';
import { FvBoxPortDataRelationAnchor } from 'src/models/anchor-and-version/fv-box-port-data-relation.model';
import { FvBoxService } from 'src/services/anchor-and-version-services/fv-box.service';
import { InternalPortDataAnchor } from 'src/models/anchor-and-version/internal-port-data.model';
import { InternalPortDataService } from 'src/services/anchor-and-version-services/internal-port-data.service';
import { NoteBoxAnchor } from 'src/models/anchor-and-version/note-box.model';
import { NoteBoxService } from 'src/services/anchor-and-version-services/note-box.service';
import { deepCopy } from 'src/utils/other.util';

interface DuplicatedDiagramChangeSet {
    newAeBoxAnchors: AeBoxAnchor[];
    newDirectWirePortDataAnchors: DirectWirePortDataAnchor[];
    newFvBoxAnchors: FvBoxAnchor[];
    newInternalPortDataAnchors: InternalPortDataAnchor[];
    newNoteBoxAnchors: NoteBoxAnchor[];
}

@Injectable({ providedIn: 'root' })
export class AllocationElementDiagramDuplicateService {
    private aeBoxService = inject(AeBoxService);
    private aedService = inject(AllocationElementDiagramService);
    private directWirePortDataService = inject(DirectWirePortDataService);
    private fvbService = inject(FvBoxService);
    private internalPortDataService = inject(InternalPortDataService);
    private noteBoxService = inject(NoteBoxService);

    public async duplicateDiagram(
        sourceAed: AllocationElementDiagramAnchor
    ): Promise<AllocationElementDiagramAnchor> {
        if (!sourceAed.validVersion) {
            throw new Error('Diagram cannot be duplicated because it has no valid version.');
        }

        const duplicatedAed = createDuplicatedAllocationElementDiagramAnchor(sourceAed);
        if (!duplicatedAed) {
            throw new Error('Diagram duplicate could not be created.');
        }

        await this.createPersistedAedDuplicate(duplicatedAed);
        await this.duplicateDiagramContents(sourceAed, duplicatedAed);

        return duplicatedAed;
    }

    public async duplicateDiagramByKey(anchorKey: number): Promise<AllocationElementDiagramAnchor> {
        const sourceAed = await this.aedService.getFullDiagramForStaticDiagramView(anchorKey);
        return this.duplicateDiagram(sourceAed);
    }

    public async duplicateDiagramContents(
        sourceAed: AllocationElementDiagramAnchor,
        duplicatedAed: AllocationElementDiagramAnchor
    ): Promise<void> {
        const changeSet = this.stageDuplicatedDiagramObjects(sourceAed, duplicatedAed.key);
        changeSet.newAeBoxAnchors.forEach((aeBox) => {
            aeBox.aedAnchorKey = duplicatedAed.key;
        });
        changeSet.newNoteBoxAnchors.forEach((noteBox) => {
            noteBox.aedAnchorKey = duplicatedAed.key;
        });
        await this.persistDuplicatedDiagramObjects(changeSet);
    }

    private addFvBoxToAeBoxRelations(fvBox: FvBoxAnchor, aeBoxAnchors: AeBoxAnchor[]): void {
        const sourceAeBox = aeBoxAnchors.find((aeBox) => aeBox.key === fvBox.sourceAeBoxAnchorKey);
        const targetAeBox = aeBoxAnchors.find((aeBox) => aeBox.key === fvBox.targetAeBoxAnchorKey);

        if (!sourceAeBox || !targetAeBox) {
            throw new Error(
                'Duplicated FV box could not be connected to its duplicated source or target AE box.'
            );
        }

        sourceAeBox.txFvBoxAnchors ??= [];
        targetAeBox.rxFvBoxAnchors ??= [];
        sourceAeBox.txFvBoxAnchors.push(fvBox);
        targetAeBox.rxFvBoxAnchors.push(fvBox);
    }

    private async createNewDirectWirePortDataAnchors(
        changeSet: DuplicatedDiagramChangeSet
    ): Promise<void> {
        if (changeSet.newDirectWirePortDataAnchors.length === 0) return;

        const directWirePortDataCopies = deepCopy(changeSet.newDirectWirePortDataAnchors);
        const temporaryKeys = changeSet.newDirectWirePortDataAnchors.map((anchor) => anchor.key);
        directWirePortDataCopies.forEach((anchor) => {
            anchor.key = 0;
            removeNestedObjects(anchor);
        });
        const newDirectWirePortData =
            await this.directWirePortDataService.createAnchors(directWirePortDataCopies);
        const directWirePortDataKeyMap = new Map<number, number>();
        newDirectWirePortData.forEach((anchor, i) => {
            directWirePortDataKeyMap.set(temporaryKeys[i], anchor.key);
            mergeNewAndExistingObjects(changeSet.newDirectWirePortDataAnchors[i], anchor);
        });
        this.replaceTemporaryDirectWirePortDataKeys(directWirePortDataKeyMap, changeSet);
    }

    private async createNewInternalPortDataAnchors(
        changeSet: DuplicatedDiagramChangeSet
    ): Promise<void> {
        if (changeSet.newInternalPortDataAnchors.length === 0) return;

        const internalPortDataCopies = deepCopy(changeSet.newInternalPortDataAnchors);
        const temporaryKeys = changeSet.newInternalPortDataAnchors.map((anchor) => anchor.key);
        internalPortDataCopies.forEach((anchor) => {
            anchor.key = 0;
            removeNestedObjects(anchor);
        });
        const newInternalPortData =
            await this.internalPortDataService.createAnchors(internalPortDataCopies);
        const internalPortDataKeyMap = new Map<number, number>();
        newInternalPortData.forEach((anchor, i) => {
            internalPortDataKeyMap.set(temporaryKeys[i], anchor.key);
            mergeNewAndExistingObjects(changeSet.newInternalPortDataAnchors[i], anchor);
        });
        this.replaceTemporaryInternalPortDataKeys(internalPortDataKeyMap, changeSet);
    }

    private async createPersistedAedDuplicate(
        duplicatedAed: AllocationElementDiagramAnchor
    ): Promise<void> {
        const aedDuplicates = [deepCopy(duplicatedAed)];
        aedDuplicates.forEach((aed) => {
            this.prepareNewAnchorVersionForCreate(aed);
            aed.key = 0;
            removeNestedObjects(aed);
        });

        const [persistedAed] = await this.aedService.createAnchors(aedDuplicates);
        if (!persistedAed) {
            throw new Error('No duplicated diagram was returned from the API.');
        }

        mergeNewAndExistingObjects(duplicatedAed, persistedAed);
    }

    private getNewPortDataRelations(
        changeSet: DuplicatedDiagramChangeSet
    ): FvBoxPortDataRelationAnchor[] {
        return changeSet.newFvBoxAnchors.flatMap((fvb) => fvb.fvBoxPortDataRelationAnchors ?? []);
    }

    private getUniqueFvBoxAnchors(aed: AllocationElementDiagramAnchor): FvBoxAnchor[] {
        const seenKeys = new Set<number>();
        const result: FvBoxAnchor[] = [];

        for (const fvBox of (aed.aeBoxAnchors ?? []).flatMap((aeBox) => [
            ...(aeBox.rxFvBoxAnchors ?? []),
            ...(aeBox.txFvBoxAnchors ?? []),
        ])) {
            if (!fvBox || seenKeys.has(fvBox.key)) continue;

            seenKeys.add(fvBox.key);
            result.push(fvBox);
        }

        return result;
    }

    private isSameDirectWireInternalPortData(
        a: DirectWirePortDataAnchor | InternalPortDataAnchor,
        b: DirectWirePortDataAnchor | InternalPortDataAnchor
    ): boolean {
        const aName = a.validVersion?.name ?? a.displayName;
        const bName = b.validVersion?.name ?? b.displayName;

        return (
            a.sourceAeAnchorKey === b.sourceAeAnchorKey &&
            a.targetAeAnchorKey === b.targetAeAnchorKey &&
            aName === bName
        );
    }

    private isSameDirectWirePortData(
        a: DirectWirePortDataAnchor,
        b: DirectWirePortDataAnchor
    ): boolean {
        return this.isSameDirectWireInternalPortData(a, b);
    }

    private isSameInternalPortData(a: InternalPortDataAnchor, b: InternalPortDataAnchor): boolean {
        return this.isSameDirectWireInternalPortData(a, b);
    }

    private async persistDuplicatedDiagramObjects(
        changeSet: DuplicatedDiagramChangeSet
    ): Promise<void> {
        if (changeSet.newAeBoxAnchors.length > 0) {
            const aebCopies = deepCopy(changeSet.newAeBoxAnchors);
            const temporaryAeBoxKeys = changeSet.newAeBoxAnchors.map((aeb) => aeb.key);
            aebCopies.forEach((aeb) => {
                this.prepareNewAnchorVersionForCreate(aeb);
                aeb.key = 0;
                removeNestedObjects(aeb);
            });
            const newAebs = await this.aeBoxService.createAnchors(aebCopies);
            const aeBoxKeyMap = new Map<number, number>();
            newAebs.forEach((x, i) => {
                aeBoxKeyMap.set(temporaryAeBoxKeys[i], x.key);
                mergeNewAndExistingObjects(changeSet.newAeBoxAnchors[i], x);
            });
            this.replaceTemporaryAeBoxKeys(aeBoxKeyMap, changeSet);
        }

        if (changeSet.newFvBoxAnchors.length > 0) {
            await this.createNewDirectWirePortDataAnchors(changeSet);
            await this.createNewInternalPortDataAnchors(changeSet);
            const fvbCopies = deepCopy(changeSet.newFvBoxAnchors);
            fvbCopies.forEach((fvb) => {
                this.prepareNewAnchorVersionForCreate(fvb);
                this.validateFvBoxAeKeysArePersisted(fvb);
                fvb.key = 0;
                removeNestedObjects(fvb);
            });
            const newFvbs = await this.fvbService.createAnchors(fvbCopies);
            newFvbs.forEach((x, i) => mergeNewAndExistingObjects(changeSet.newFvBoxAnchors[i], x));
        }

        if (changeSet.newNoteBoxAnchors.length > 0) {
            const nbCopies = deepCopy(changeSet.newNoteBoxAnchors);
            nbCopies.forEach((nb) => {
                this.prepareNewAnchorVersionForCreate(nb);
                nb.key = 0;
                removeNestedObjects(nb);
            });
            const newNbs = await this.noteBoxService.createAnchors(nbCopies);
            newNbs.forEach((x, i) => mergeNewAndExistingObjects(changeSet.newNoteBoxAnchors[i], x));
        }
    }

    private prepareNewAnchorVersionForCreate<
        TAnchor extends { allVersions: TVersion[] | null; validVersion: TVersion | null },
        TVersion extends { anchorKey: number; key: number },
    >(anchor: TAnchor): void {
        if (!anchor.validVersion) return;

        const version = deepCopy(anchor.validVersion);
        version.anchorKey = 0;
        version.key = 0;
        anchor.allVersions = [version];
    }

    private replaceTemporaryAeBoxKey(key: number, aeBoxKeyMap: Map<number, number>): number {
        return aeBoxKeyMap.get(key) ?? key;
    }

    private replaceTemporaryAeBoxKeys(
        aeBoxKeyMap: Map<number, number>,
        changeSet: DuplicatedDiagramChangeSet
    ): void {
        if (!aeBoxKeyMap.size) return;

        changeSet.newFvBoxAnchors.forEach((fvb) => {
            fvb.sourceAeBoxAnchorKey = this.replaceTemporaryAeBoxKey(
                fvb.sourceAeBoxAnchorKey,
                aeBoxKeyMap
            );
            fvb.targetAeBoxAnchorKey = this.replaceTemporaryAeBoxKey(
                fvb.targetAeBoxAnchorKey,
                aeBoxKeyMap
            );
        });
    }

    private replaceTemporaryDirectWirePortDataKeys(
        keyMap: Map<number, number>,
        changeSet: DuplicatedDiagramChangeSet
    ): void {
        if (!keyMap.size) return;

        this.getNewPortDataRelations(changeSet).forEach((relation) => {
            const key = relation.directWirePortDataAnchorKey;
            if (key != null && keyMap.has(key)) {
                relation.directWirePortDataAnchorKey = keyMap.get(key)!;
            }
        });
    }

    private replaceTemporaryInternalPortDataKeys(
        keyMap: Map<number, number>,
        changeSet: DuplicatedDiagramChangeSet
    ): void {
        if (!keyMap.size) return;

        this.getNewPortDataRelations(changeSet).forEach((relation) => {
            const key = relation.internalPortDataAnchorKey;
            if (key != null && keyMap.has(key)) {
                relation.internalPortDataAnchorKey = keyMap.get(key)!;
            }
        });
    }

    private stageDuplicatedDiagramObjects(
        sourceAed: AllocationElementDiagramAnchor,
        duplicatedAedKey: number
    ): DuplicatedDiagramChangeSet {
        const duplicatedAeBoxes: AeBoxAnchor[] = [];
        const aeBoxKeyMap = new Map<number, number>();

        for (const aeBox of sourceAed.aeBoxAnchors ?? []) {
            const duplicatedAeBox = createDuplicatedAeBoxAnchor(aeBox, duplicatedAedKey, {
                positionOffset: 0,
            });
            if (!duplicatedAeBox) {
                throw new Error('Duplicated AE box could not be created.');
            }

            duplicatedAeBox.key = getNextTemporaryAnchorKey(duplicatedAeBoxes);
            duplicatedAeBox.rxFvBoxAnchors = [];
            duplicatedAeBox.txFvBoxAnchors = [];
            duplicatedAeBoxes.push(duplicatedAeBox);
            aeBoxKeyMap.set(aeBox.key, duplicatedAeBox.key);
        }

        const changeSet: DuplicatedDiagramChangeSet = {
            newAeBoxAnchors: duplicatedAeBoxes,
            newDirectWirePortDataAnchors: [],
            newFvBoxAnchors: [],
            newInternalPortDataAnchors: [],
            newNoteBoxAnchors: [],
        };

        for (const fvBox of this.getUniqueFvBoxAnchors(sourceAed)) {
            const sourceAeBoxKey = aeBoxKeyMap.get(fvBox.sourceAeBoxAnchorKey);
            const targetAeBoxKey = aeBoxKeyMap.get(fvBox.targetAeBoxAnchorKey);

            if (sourceAeBoxKey == null || targetAeBoxKey == null) {
                throw new Error(
                    'Diagram cannot be duplicated because an FV box points outside this diagram.'
                );
            }

            const duplicatedFvBox = createDuplicatedFvBoxAnchor(fvBox, { positionOffset: 0 });
            if (!duplicatedFvBox) {
                throw new Error('Duplicated FV box could not be created.');
            }

            duplicatedFvBox.key = getNextTemporaryAnchorKey(changeSet.newFvBoxAnchors);
            duplicatedFvBox.sourceAeBoxAnchorKey = sourceAeBoxKey;
            duplicatedFvBox.targetAeBoxAnchorKey = targetAeBoxKey;
            duplicatedFvBox.fvBoxPortDataRelationAnchors?.forEach((relation) => {
                relation.fvBoxAnchorKey = duplicatedFvBox.key;
            });
            duplicatedFvBox.gatewayAnchors?.forEach((gateway) => {
                gateway.fvBoxAnchorKey = duplicatedFvBox.key;
            });

            this.trackNewDirectWireInternalPortData(duplicatedFvBox, changeSet);
            this.addFvBoxToAeBoxRelations(duplicatedFvBox, duplicatedAeBoxes);
            changeSet.newFvBoxAnchors.push(duplicatedFvBox);
        }

        changeSet.newNoteBoxAnchors = (sourceAed.noteBoxAnchors ?? []).map((noteBox, index) => {
            const duplicatedNoteBox = createDuplicatedNoteBoxAnchor(noteBox, duplicatedAedKey, {
                positionOffset: 0,
            });
            if (!duplicatedNoteBox) {
                throw new Error('Duplicated notebox could not be created.');
            }

            duplicatedNoteBox.key = -index - 1;
            return duplicatedNoteBox;
        });

        return changeSet;
    }

    private trackNewDirectWireInternalPortData(
        fvBoxAnchor: FvBoxAnchor,
        changeSet: DuplicatedDiagramChangeSet
    ): void {
        for (const relation of fvBoxAnchor.fvBoxPortDataRelationAnchors ?? []) {
            const directWirePortData = relation.directWirePortDataAnchor;
            if (directWirePortData && directWirePortData.key <= 0) {
                const existing = changeSet.newDirectWirePortDataAnchors.find((anchor) =>
                    this.isSameDirectWirePortData(anchor, directWirePortData)
                );
                const trackedPortData = existing ?? directWirePortData;

                if (!existing) {
                    trackedPortData.key = -changeSet.newDirectWirePortDataAnchors.length - 1;
                    changeSet.newDirectWirePortDataAnchors.push(trackedPortData);
                }

                relation.directWirePortDataAnchor = trackedPortData;
                relation.directWirePortDataAnchorKey = trackedPortData.key;
            }

            const internalPortData = relation.internalPortDataAnchor;
            if (internalPortData && internalPortData.key <= 0) {
                const existing = changeSet.newInternalPortDataAnchors.find((anchor) =>
                    this.isSameInternalPortData(anchor, internalPortData)
                );
                const trackedPortData = existing ?? internalPortData;

                if (!existing) {
                    trackedPortData.key = -changeSet.newInternalPortDataAnchors.length - 1;
                    changeSet.newInternalPortDataAnchors.push(trackedPortData);
                }

                relation.internalPortDataAnchor = trackedPortData;
                relation.internalPortDataAnchorKey = trackedPortData.key;
            }
        }
    }

    private validateFvBoxAeKeysArePersisted(anchor: FvBoxAnchor): void {
        if (anchor.sourceAeBoxAnchorKey <= 0 || anchor.targetAeBoxAnchorKey <= 0) {
            throw new Error(
                'FV Box source/target AE keys must be saved before updating FV Box references.'
            );
        }
    }
}
